'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgTabMoreLeft from '@common/icon/SvgTabMoreLeft';
import SvgTabMoreRight from '@common/icon/SvgTabMoreRight';
import { cn } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { chunk } from 'es-toolkit';
import { useReducedMotion } from 'framer-motion';
import { useRef, useState } from 'react';
import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import type { WEBVIEW_TYPE_ENTRY } from '@/constants/entry';
import { WEEKLY_TOP_COLUMN_CLASS_NAME, WEEKLY_TOP_LIST_CLASS_NAME } from './weeklyTopClassNames';
import { WEEKLY_TOP_ALL_GROUP_CODE, WEEKLY_TOP_ROWS_PER_COLUMN, type WeeklyTopGroup, type WeeklyTopGroupCode, type WeeklyTopItem } from './weeklyTopItem';
import { WeeklyTopRankingItem } from './WeeklyTopRankingItem';
const CATEGORY_SCROLL_DURATION_MS = 560;
const ARROW_CLASS_NAME = 'absolute top-1/2 size-fit -translate-y-1/2 rounded-full p-0 transition-opacity motion-reduce:transition-none mobile-tablet:hidden';
const ARROW_VISIBLE_CLASS_NAME = 'opacity-100 duration-[240ms] ease-out';
const ARROW_HIDDEN_CLASS_NAME = 'pointer-events-none opacity-0 duration-200 ease-in';
const easeInOutCubic = (progress: number) => progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
interface WeeklyTopRankingProps {
  groups: WeeklyTopGroup[];
  items: WeeklyTopItem[];
  entry: WEBVIEW_TYPE_ENTRY;
}
export const WeeklyTopRanking = t0 => {
  const $ = _c(54);
  const {
    groups,
    items,
    entry
  } = t0;
  const [selectedGroupCode, setSelectedGroupCode] = useState(WEEKLY_TOP_ALL_GROUP_CODE);
  const isAllGroup = selectedGroupCode === WEEKLY_TOP_ALL_GROUP_CODE;
  const shouldReduceMotion = useReducedMotion();
  const categoryRowRef = useRef(null);
  const scrollFrameRef = useRef(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = node => {
      setCanScrollPrev(node.scrollLeft > 1);
      setCanScrollNext(node.scrollLeft + node.clientWidth < node.scrollWidth - 1);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const updateScrollEdge = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = node_0 => {
      categoryRowRef.current = node_0;
      if (!node_0) {
        return;
      }
      const observer = new ResizeObserver(() => updateScrollEdge(node_0));
      observer.observe(node_0);
      return () => observer.disconnect();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const observeCategoryRow = t2;
  let t3;
  if ($[2] !== shouldReduceMotion) {
    t3 = direction => {
      const node_1 = categoryRowRef.current;
      if (!node_1) {
        return;
      }
      cancelAnimationFrame(scrollFrameRef.current);
      const from = node_1.scrollLeft;
      const to = Math.min(Math.max(from + direction * node_1.clientWidth, 0), node_1.scrollWidth - node_1.clientWidth);
      const startTime = performance.now();
      const step = now => {
        const progress = shouldReduceMotion ? 1 : Math.min((now - startTime) / CATEGORY_SCROLL_DURATION_MS, 1);
        node_1.scrollLeft = from + (to - from) * easeInOutCubic(progress);
        if (progress < 1) {
          scrollFrameRef.current = requestAnimationFrame(step);
        }
      };
      scrollFrameRef.current = requestAnimationFrame(step);
    };
    $[2] = shouldReduceMotion;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scrollCategoryRow = t3;
  let t4;
  if ($[4] !== entry || $[5] !== selectedGroupCode) {
    t4 = UnauthQueryOptions["get:/wello/v2/allows/home/gift-product/weekly-top"]({
      query: {
        giftCategoryGroupType: selectedGroupCode,
        entry
      }
    });
    $[4] = entry;
    $[5] = selectedGroupCode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = !isAllGroup;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      ...t4,
      select: _temp,
      enabled: t5,
      placeholderData: keepPreviousData
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: groupItems,
    isLoadingError,
    isPlaceholderData,
    refetch
  } = useQuery(t6);
  const isSwitching = !isAllGroup && (isPlaceholderData || !groupItems);
  let t7;
  if ($[10] !== canScrollNext || $[11] !== canScrollPrev || $[12] !== groupItems || $[13] !== groups || $[14] !== isAllGroup || $[15] !== isLoadingError || $[16] !== isSwitching || $[17] !== items || $[18] !== refetch || $[19] !== scrollCategoryRow || $[20] !== selectedGroupCode) {
    const columns = chunk(isAllGroup ? items : groupItems ?? items, WEEKLY_TOP_ROWS_PER_COLUMN);
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = event => updateScrollEdge(event.currentTarget);
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== groups || $[24] !== selectedGroupCode) {
      let t10;
      if ($[26] !== selectedGroupCode) {
        t10 = t11 => {
          const {
            groupCode,
            groupName,
            imageUrl
          } = t11;
          const isSelected = selectedGroupCode === groupCode;
          return <button key={groupCode} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelectedGroupCode(groupCode as WeeklyTopGroupCode)} className={cn("flex min-w-56 shrink-0 flex-col items-center gap-4 rounded-medium border px-8 py-4", INTERACTIVE_SURFACE, isSelected ? "border-Gray-300 bg-Gray-white" : "border-transparent")}><Image src={imageUrl} alt="" width={104} height={104} className="size-52 object-contain" /><span className={cn("whitespace-nowrap font-600 typo-caption1", isSelected ? "text-Gray-900" : "text-Gray-600")}>{groupName}</span></button>;
        };
        $[26] = selectedGroupCode;
        $[27] = t10;
      } else {
        t10 = $[27];
      }
      t9 = groups.map(t10);
      $[23] = groups;
      $[24] = selectedGroupCode;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[28] !== t9) {
      t10 = <div ref={observeCategoryRow} role="radiogroup" aria-label={"\uC778\uAE30 \uB2F5\uB840\uD488 \uBD84\uB958"} onScroll={t8} className="flex w-full gap-4 overflow-x-auto hide-scrollbar mobile-tablet:px-20">{t9}</div>;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    const t11 = !canScrollPrev;
    const t12 = canScrollPrev ? undefined : -1;
    const t13 = canScrollPrev ? ARROW_VISIBLE_CLASS_NAME : ARROW_HIDDEN_CLASS_NAME;
    let t14;
    if ($[30] !== t13) {
      t14 = cn(ARROW_CLASS_NAME, "left-40 -translate-x-1/2", t13);
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== scrollCategoryRow) {
      t15 = () => scrollCategoryRow(-1);
      $[32] = scrollCategoryRow;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <SvgTabMoreLeft className="size-27" originalFill={true} />;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    let t17;
    if ($[35] !== t11 || $[36] !== t12 || $[37] !== t14 || $[38] !== t15) {
      t17 = <Button aria-label={"\uC774\uC804 \uBD84\uB958"} aria-hidden={t11} tabIndex={t12} theme="sub-text" className={t14} onClick={t15}>{t16}</Button>;
      $[35] = t11;
      $[36] = t12;
      $[37] = t14;
      $[38] = t15;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    const t18 = !canScrollNext;
    const t19 = canScrollNext ? undefined : -1;
    const t20 = canScrollNext ? ARROW_VISIBLE_CLASS_NAME : ARROW_HIDDEN_CLASS_NAME;
    let t21;
    if ($[40] !== t20) {
      t21 = cn(ARROW_CLASS_NAME, "right-40 translate-x-1/2", t20);
      $[40] = t20;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    let t22;
    if ($[42] !== scrollCategoryRow) {
      t22 = () => scrollCategoryRow(1);
      $[42] = scrollCategoryRow;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    let t23;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <SvgTabMoreRight className="size-27" originalFill={true} />;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    let t24;
    if ($[45] !== t18 || $[46] !== t19 || $[47] !== t21 || $[48] !== t22) {
      t24 = <Button aria-label={"\uB2E4\uC74C \uBD84\uB958"} aria-hidden={t18} tabIndex={t19} theme="sub-text" className={t21} onClick={t22}>{t23}</Button>;
      $[45] = t18;
      $[46] = t19;
      $[47] = t21;
      $[48] = t22;
      $[49] = t24;
    } else {
      t24 = $[49];
    }
    let t25;
    if ($[50] !== t10 || $[51] !== t17 || $[52] !== t24) {
      t25 = <div className="relative w-full px-40 content-container mobile-tablet:px-0">{t10}{t17}{t24}</div>;
      $[50] = t10;
      $[51] = t17;
      $[52] = t24;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    t7 = <>{t25}{!isAllGroup && isLoadingError ? <div className="flex w-full flex-col items-center gap-16 px-40 content-container mobile-tablet:px-20 mobile-tablet:py-40"><p className="text-center text-Gray-600 typo-body3 mobile-tablet:typo-body4">답례품을 불러오지 못했어요.<br />잠시 후 다시 시도해 주세요.</p><Button theme="primary-line" dimensions="H40" onClick={() => refetch()}>다시 시도</Button></div> : columns.length === 0 ? <p className="w-full px-40 text-center text-Gray-600 typo-body3 content-container mobile-tablet:px-20 mobile-tablet:py-40 mobile-tablet:typo-body4">조회된 답례품이 없어요.</p> : <div aria-busy={isSwitching} className={cn(WEEKLY_TOP_LIST_CLASS_NAME, "transition-opacity duration-200", isSwitching && "opacity-60")}>{columns.map(_temp3)}<span aria-hidden="true" className="hidden w-4 shrink-0 max-[639px]:block" /></div>}</>;
    $[10] = canScrollNext;
    $[11] = canScrollPrev;
    $[12] = groupItems;
    $[13] = groups;
    $[14] = isAllGroup;
    $[15] = isLoadingError;
    $[16] = isSwitching;
    $[17] = items;
    $[18] = refetch;
    $[19] = scrollCategoryRow;
    $[20] = selectedGroupCode;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
function _temp(data) {
  return data.context?.contents ?? [];
}
function _temp2(item) {
  return <li key={item.productNumber} value={item.rank}><WeeklyTopRankingItem item={item} /></li>;
}
function _temp3(column) {
  return <ol key={column[0].rank} className={WEEKLY_TOP_COLUMN_CLASS_NAME}>{column.map(_temp2)}</ol>;
}